import { FC } from 'react'
import { Trash } from 'lucide-react'
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import FormGroup from '@/core/components/FormGroup'

interface ApplePayKeysProps {
  // Full RHF path to the applePayConfig object (e.g.
  // `groups.0.festivals.1.festivalPaymentProviderDtos.2.applePayConfig`).
  namePrefix: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>
}

// Merchant identity certificate + private key for one Apple Pay config.
// Selection and removal are staged in form state and applied on save (the config
// create/update endpoints accept the key files as multipart parts), so no
// separate upload request is needed here.
export const ApplePayKeys: FC<ApplePayKeysProps> = ({
  namePrefix,
  control,
  setValue,
}) => {
  const config = useWatch({ control, name: namePrefix })

  const hasCertificate = config?.hasCertificate
  const hasPrivateKey = config?.hasPrivateKey
  const certificateFile = config?.certificateFile
  const privateKeyFile = config?.privateKeyFile
  const clearKeys = config?.clearKeys
  const hasStoredKey = hasCertificate || hasPrivateKey

  const toggleClearKeys = () => {
    const next = !clearKeys
    setValue(`${namePrefix}.clearKeys`, next, { shouldDirty: true })
    if (next) {
      // Removing the stored pair and staging a new upload are mutually exclusive.
      setValue(`${namePrefix}.certificateFile`, null, { shouldDirty: true })
      setValue(`${namePrefix}.privateKeyFile`, null, { shouldDirty: true })
    }
  }

  const status = (present?: boolean) =>
    present ? (
      <span className="text-accent-2">Uploaded ✓</span>
    ) : (
      <span className="text-monochrome-1">Not uploaded</span>
    )

  return (
    <div className="space-y-4 border-t border-monochrome-120 pt-4">
      <h4 className="text-sm font-medium text-monochrome-1">
        Merchant identity keys
      </h4>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md border border-monochrome-120 p-3">
          <p className="text-body-small text-monochrome-1 mb-1">
            Certificate (.pem)
          </p>
          <p className="text-body-medium text-monochrome-1 font-medium">
            {status(hasCertificate)}
          </p>
        </div>
        <div className="rounded-md border border-monochrome-120 p-3">
          <p className="text-body-small text-monochrome-1 mb-1">
            Private key (.key / .pem)
          </p>
          <p className="text-body-medium text-monochrome-1 font-medium">
            {status(hasPrivateKey)}
          </p>
        </div>
      </div>

      {clearKeys ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-body-small text-accent-3">
            Stored keys will be removed on save.
          </p>
          <Button type="button" variant="secondary" onClick={toggleClearKeys}>
            Undo
          </Button>
        </div>
      ) : (
        <>
          <Controller
            name={`${namePrefix}.certificateFile`}
            control={control}
            render={({ field }) => (
              <FormGroup label="Certificate file (.pem)">
                <input
                  type="file"
                  accept=".pem"
                  onChange={(e) => field.onChange(e.target.files?.[0] ?? null)}
                  className="w-full rounded-md border border-monochrome-120 bg-monochrome-3-60 px-3 py-2 text-body-medium text-monochrome-1 file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-monochrome-120 file:px-3 file:py-1 file:text-body-small file:text-monochrome-1"
                />
              </FormGroup>
            )}
          />

          <Controller
            name={`${namePrefix}.privateKeyFile`}
            control={control}
            render={({ field }) => (
              <FormGroup label="Private key file (.key / .pem)">
                <input
                  type="file"
                  accept=".key,.pem"
                  onChange={(e) => field.onChange(e.target.files?.[0] ?? null)}
                  className="w-full rounded-md border border-monochrome-120 bg-monochrome-3-60 px-3 py-2 text-body-medium text-monochrome-1 file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-monochrome-120 file:px-3 file:py-1 file:text-body-small file:text-monochrome-1"
                />
              </FormGroup>
            )}
          />

          <div className="flex items-center justify-between gap-2">
            <p className="text-body-small text-monochrome-1">
              {certificateFile || privateKeyFile
                ? 'Selected keys will be uploaded on save.'
                : 'Select both files to upload, then save.'}
            </p>

            {hasStoredKey && (
              <Button
                type="button"
                variant="destructive"
                onClick={toggleClearKeys}
              >
                <Trash size={16} className="mr-1" />
                Remove keys
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
